// Angular
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';

// Angular Material
import { MatMenuModule } from '@angular/material/menu';

// RxJS
import { map, take } from 'rxjs';

// Third-party
import { environment } from '@environment';
import { ROUTES } from '@routes';

// Data Contracts
import { UserPermissionList } from '@data-contracts';

// Local
import { EmulationApiService } from '@app/services/api/emulation/emulation-api.service';
import { IdentityClaimsService } from '@app/services/identity-claims/identity-claims.service';
import { UserManagementApiService } from '@app/services/api/user-management/user-management.service';
import { TrialBindersStateService } from '@app/trialbinder/services/ui-state/trial-binders-state.service';
import { FsxContact } from '@app/types/user/User';
import { UserGroup } from '@app/types/user/user-permissions';
import { AuthService } from '@core/auth/auth.service';
import { FILING_ROUTES } from '@feature/filing/filing-routes.constants';
import { MAILBOX_TAB } from '@app/pages/mailbox/mailbox-routes.constants';
import { buildClassicUrl } from '@shared/utils/classic-url.util';
import { DelayedHandle, scheduleDelayed } from '@shared/utils/schedule-delayed';

@Component({
  selector: 'app-navbar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, MatMenuModule],
  templateUrl: './navbar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./navbar.component.scss'],
})
export class NavbarComponent implements OnInit, AfterViewInit {
  /*********************************************************************************************************************
   * Service Injections
   ********************************************************************************************************************/
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly trialBinderStateService = inject(TrialBindersStateService);
  private readonly userManagementApiService = inject(UserManagementApiService);
  private readonly emulationApiService = inject(EmulationApiService);
  private readonly identityClaimsService = inject(IdentityClaimsService);
  /**
   * Marks the view for check after async/timer updates (OnPush).
   */
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  /**
   * DestroyRef used to disconnect the nav scroll ResizeObserver on teardown.
   */
  private readonly destroyRef = inject(DestroyRef);

  /**
   * Filing Trigger Ref to get the filing trigger element.
   */
  @ViewChild('filingTrigger') filingTriggerRef!: ElementRef<HTMLElement>;

  /**
   * Scroll container for the middle nav icons (logo/contact stay outside this region).
   */
  @ViewChild('navScroll') navScrollRef?: ElementRef<HTMLElement>;

  /**
   * Pixels to move the middle nav list per chevron click (roughly one icon row).
   */
  private static readonly NAV_SCROLL_STEP_PX = 64;

  /**
   * Whether the middle nav can scroll upward (chevron-up visibility).
   */
  readonly canScrollUp = signal(false);

  /**
   * Whether the middle nav can scroll downward (chevron-down visibility).
   */
  readonly canScrollDown = signal(false);

  /**
   * ResizeObserver watching the scroll viewport and list for overflow changes.
   */
  private navScrollResizeObserver: ResizeObserver | null = null;

  /**
   * Routes to navigate to the home routes.
   */
  ROUTES = ROUTES;

  /**
   * Filing Routes to navigate to the filing routes.
   */
  FILING_ROUTES = FILING_ROUTES;

  /**
   * Mailbox tabs (Inbox / Sent Items / Rejected Items) for the envelope fly-out.
   */
  MAILBOX_TAB = MAILBOX_TAB;

  /**
   * The Classic File & ServeXpress login URL (Login/Login.aspx?oauth=true),
   * environment-specific. All Classic navigation must go through this URL so
   * the legacy session is established; deep links ride along via returnURL
   * (Login -> WebServer/Default.asp -> Home.aspx redirects to returnURL).
   */
  readonly classicLoginUrl = environment.iframeUrl;

  /**
   * The Classic search report destinations (legacy SearchReport pages).
   * `s` is the selected Classic search tab: 1 Case History, 2 Cases Search,
   * 3 Daily Docket, 4 Transaction Status, 5 Advanced Search.
   */
  readonly classicSearchLinks = [
    {
      label: 'Advanced Search',
      path: '/SearchReport/FilingSearch.aspx?t=4&s=5',
    },
    {
      label: 'Case History',
      path: '/SearchReport/DocsByCase.aspx?t=4&s=1',
    },
    { label: 'Cases Search', path: '/SearchReport/CasesReport.aspx?t=4&s=2' },
    { label: 'Daily Docket', path: '/SearchReport/DailyDocket.aspx?t=4&s=3' },
    {
      label: 'Transaction Status',
      path: '/SearchReport/FilingStatus.aspx?t=4&s=4',
    },
  ];

  /**
   * The Classic Alerts destination (legacy law-firm alerts tab,
   * per LNMenuConfiguration.xml / UserFeatures.xml AlertsURL).
   */
  readonly classicAlertsPath = '/Home/HomeOUI.aspx?t=3&s=0&p=alerts';

  /**
   * The Classic Inbox destination; also the My Filings rail icon's direct link.
   */
  readonly classicInboxPath = '/Home/DisplayFilingList.aspx?t=1&s=1&go=inbox';

  /**
   * The Classic filing list destinations (legacy Home + DisplayFilingList views).
   */
  readonly classicFilingsLinks = [
    { label: 'Home', path: '/Home/Home.aspx?oldui=true' },
    { label: 'Inbox', path: '/Home/DisplayFilingList.aspx?t=1&s=1&go=inbox' },
    { label: 'Outbox', path: '/Home/DisplayFilingList.aspx?t=1&s=1&go=outbox' },
    {
      label: 'Rejected Items',
      path: '/Home/DisplayFilingList.aspx?t=1&s=1&go=rejected',
    },
    {
      label: 'Case & Party Management',
      path: '/Home/HomeOUI.aspx?t=1&s=2&p=casemgmt',
    },
  ];

  /**
   * Builds a link into Classic File & ServeXpress that establishes the legacy
   * session via Login/Login.aspx?oauth=true and then redirects to the
   * requested page through the legacy returnURL support.
   *
   * @param path The Classic root-relative path (including querystring).
   */
  classicUrl(path: string): string {
    return buildClassicUrl(path);
  }

  /**
   * Filing Menu Visible used to show/hide the filing menu.
   */
  readonly filingMenuVisible = signal(false);

  /**
   * Filing Menu Position used to position the filing menu.
   */
  readonly filingMenuPosition = signal({ left: 0, top: 0 });

  /**
   * Pending hover-hide for the filing menu (RxJS `timer`; visibility is a signal, so CD does not depend on Zone).
   */
  private filingMenuHideHandle: DelayedHandle | null = null;

  /**
   * Generic hover menu for nav items that replaced tooltips
   */
  readonly hoverMenuId = signal<string | null>(null);

  /**
   * Hover Menu Position to get the hover menu position.
   */
  readonly hoverMenuPosition = signal({ left: 0, top: 0 });

  /**
   * Pending hover-hide for the generic nav fly-out (RxJS `timer`; visibility is a signal, so CD does not depend on Zone).
   */
  private hoverMenuHideHandle: DelayedHandle | null = null;

  /**
   * True for Form 50 Agents and self-represented individuals, who see the Organization Settings
   * nav item as "Account Settings" with a user-gear icon.
   */
  readonly isAccountSettingsUser =
    this.identityClaimsService.isAccountSettingsUser;

  /**
   * Label for the Organization Settings nav item (link text and aria-label).
   */
  readonly orgSettingsLabel = computed(() =>
    this.isAccountSettingsUser() ? 'Account Settings' : 'Organization Settings',
  );

  /**
   * A boolean flag used to check if the user is an internal user.
   */
  isInternalUser = false;

  /**
   * A boolean flag used to check if the user is a court user.
   */
  isCourtUser = false;

  /**
   * A boolean flag used to check if the user is a law firm user.
   */
  isLawFirmUser = false;

  /**
   * A boolean flag used to check if the user can modify the organization.
   */
  canModifyOrg = false;

  /**
   * A boolean flag used to check if the user is loading.
   */
  loadingUser = true;

  /**
   * A boolean flag used to check if the user can access the clerk review.
   */
  canAccessClerkReview = false;

  /**
   * A boolean flag used to check if the user can access the judge review.
   */
  canAccessJudgeReview = false;

  /**
   * Whether to show the Message Board nav item. False for court users without
   * Search on a court in their courthouse that has an active message board.
   * Starts true so the icon stays while permissions load or if the call fails.
   */
  canAccessMessageBoard = true;

  /**
   * Where the Judge Review nav item goes: the Judge Chambers page when the
   * user's court enables judge chambers, otherwise the Judge Review list.
   */
  judgeReviewRoute: string = ROUTES.JUDGE_REVIEW_LIST;

  /**
   * Whether the current user has any capability to emulate other users. Controls visibility of the
   * "Emulate a User" entry in the System Administration menu.
   */
  canEmulate = false;

  /**
   * OnInit lifecycle hook to set the icon access.
   */
  ngOnInit(): void {
    this.authService.sessionUser.subscribe((user: FsxContact) => {
      this.loadingUser = user == null;
      this.setIconAccess();
      this.changeDetectorRef.markForCheck();
    });

    // Capability is enforced server-side; a non-privileged user simply gets false.
    this.emulationApiService
      .getEnabled()
      .pipe(take(1))
      .subscribe((canEmulate: boolean) => {
        this.canEmulate = canEmulate;
        this.changeDetectorRef.markForCheck();
      });
  }

  /**
   * AfterViewInit lifecycle hook to observe the middle nav scroll region for overflow.
   */
  ngAfterViewInit(): void {
    const scrollEl = this.navScrollRef?.nativeElement;
    if (!scrollEl) {
      return;
    }

    this.updateNavScrollState();

    this.navScrollResizeObserver = new ResizeObserver(() => {
      this.updateNavScrollState();
    });
    this.navScrollResizeObserver.observe(scrollEl);
    const listEl = scrollEl.querySelector('ul');
    if (listEl) {
      this.navScrollResizeObserver.observe(listEl);
    }

    this.destroyRef.onDestroy(() => {
      this.navScrollResizeObserver?.disconnect();
      this.navScrollResizeObserver = null;
    });
  }

  /**
   * Logout the user.
   */
  logout(): void {
    this.authService.logout();
  }

  /**
   * Updates chevron visibility from the middle nav scroll position and overflow.
   */
  updateNavScrollState(): void {
    const el = this.navScrollRef?.nativeElement;
    if (!el) {
      this.canScrollUp.set(false);
      this.canScrollDown.set(false);
      return;
    }

    const epsilon = 1;
    this.canScrollUp.set(el.scrollTop > epsilon);
    this.canScrollDown.set(
      el.scrollTop + el.clientHeight < el.scrollHeight - epsilon,
    );
  }

  /**
   * Handles middle-nav scroll (wheel/trackpad/chevrons): dismiss stale flyouts, then refresh chevrons.
   * Kept separate from updateNavScrollState so ResizeObserver layout updates do not close menus.
   */
  onNavScroll(): void {
    this.filingMenuVisible.set(false);
    this.hoverMenuId.set(null);
    this.updateNavScrollState();
  }

  /**
   * Scrolls the middle nav icons by one step in the given direction.
   * @param direction -1 scrolls up; 1 scrolls down.
   */
  scrollNav(direction: -1 | 1): void {
    const el = this.navScrollRef?.nativeElement;
    if (!el) {
      return;
    }

    el.scrollBy({
      top: direction * NavbarComponent.NAV_SCROLL_STEP_PX,
      behavior: 'smooth',
    });
  }

  /**
   * Set the icon access.
   */
  setIconAccess() {
    this.userManagementApiService
      .getUserPermissions()
      .pipe(
        take(1),
        map((permissions: UserPermissionList) => {
          const claimsPermissions = this.authService.userPermissions;

          this.isCourtUser = claimsPermissions.userType == UserGroup.Court;
          this.isLawFirmUser = claimsPermissions.userType == UserGroup.LawFirm;

          this.isInternalUser = claimsPermissions.isInternalUser;

          // If the session user is can modify the organization they can have access
          this.canModifyOrg = permissions.canModifyOrganization ?? false;
          this.canAccessClerkReview = permissions.canAccessClerkReview ?? false;
          this.canAccessJudgeReview = permissions.canAccessJudgeReview ?? false;
          // An API without the flag predates the court-user rule, so keep the icon.
          this.canAccessMessageBoard =
            permissions.canAccessMessageBoard ?? true;
          this.judgeReviewRoute = permissions.isJudgeChambersEnabled
            ? ROUTES.JUDGE_CHAMBERS
            : ROUTES.JUDGE_REVIEW_LIST;
        }),
      )
      .subscribe(() => {
        this.changeDetectorRef.markForCheck();
        // Permission-gated icons change list height; refresh chevron visibility after render.
        queueMicrotask(() => this.updateNavScrollState());
      });
  }

  /**
   * A boolean flag used to check if the user is on a filing route.
   */
  get isFilingRouteActive(): boolean {
    return this.router.url.startsWith('/' + this.ROUTES.FILING);
  }

  /**
   * Navigate to a filing route with a clean URL (e.g. /filing/existing-case).
   * Uses navigateByUrl so the full URL is replaced and no outlet segments are retained.
   */
  navigateToRoute(filingRoute: string): void {
    const url = `/${this.ROUTES.FILING}/${filingRoute}`;
    this.router.navigateByUrl(url);
  }

  /**
   * Guards against an Escape-triggered refocus of a trigger re-opening the
   * very menu Escape just closed - the trigger's own (focus) handler would
   * otherwise fire synchronously inside the .focus() call below and undo
   * the close before the caller ever sees it.
   */
  private suppressTriggerFocusOpen = false;

  /**
   * Show the filing menu.
   */
  showFilingMenu(): void {
    if (this.suppressTriggerFocusOpen) {
      return;
    }
    this.cancelHideFilingMenu();
    const el = this.filingTriggerRef?.nativeElement;
    if (el) {
      const rect = el.getBoundingClientRect();
      // Align first link with icon center; additional links stack below (no translateY(-50%))
      const firstLinkOffsetPx = 26;
      this.filingMenuPosition.set({
        left: rect.right + 8,
        top: rect.top + rect.height / 2 - firstLinkOffsetPx,
      });
    }
    this.filingMenuVisible.set(true);
  }

  /**
   * Handles the filing group menu losing focus
   * @param event
   */
  onFilingGroupFocusOut(event: FocusEvent): void {
    const container = event.currentTarget as HTMLElement;
    const next = event.relatedTarget as Node | null;
    if (!next || !container.contains(next)) {
      this.hideFilingMenu();
    }
  }

  /**
   * Closes the filing menu on Escape and returns focus to its trigger -
   * the standard disclosure/menu keyboard affordance. Closes immediately
   * (bypassing the mouseleave debounce) since this is an explicit dismissal.
   */
  onFilingGroupEscape(): void {
    this.cancelHideFilingMenu();
    this.filingMenuVisible.set(false);
    const trigger =
      this.filingTriggerRef?.nativeElement.querySelector<HTMLElement>('a');
    if (trigger) {
      this.suppressTriggerFocusOpen = true;
      trigger.focus();
      this.suppressTriggerFocusOpen = false;
    }
  }

  /**
   * Cancel the hide filing menu.
   */
  cancelHideFilingMenu(): void {
    this.filingMenuHideHandle?.cancel();
    this.filingMenuHideHandle = null;
  }

  /**
   * Hide the filing menu.
   */
  hideFilingMenu(): void {
    this.cancelHideFilingMenu();
    this.filingMenuHideHandle = scheduleDelayed(
      150,
      () => {
        this.filingMenuVisible.set(false);
        this.filingMenuHideHandle = null;
      },
      this.destroyRef,
    );
  }

  /**
   * Show the hover menu.
   */
  showHoverMenu(menuId: string, triggerEl: EventTarget | null): void {
    if (this.suppressTriggerFocusOpen) {
      return;
    }
    this.cancelHideHoverMenu();
    const el = triggerEl as HTMLElement;
    if (el) {
      const rect = el.getBoundingClientRect();
      // Align first link with icon center; additional links stack below (no translateY(-50%))
      const firstLinkOffsetPx = 26;
      this.hoverMenuPosition.set({
        left: rect.right + 8,
        top: rect.top + rect.height / 2 - firstLinkOffsetPx,
      });
    }
    this.hoverMenuId.set(menuId);
  }

  /**
   * Cancel the hide hover menu.
   */
  cancelHideHoverMenu(): void {
    this.hoverMenuHideHandle?.cancel();
    this.hoverMenuHideHandle = null;
  }

  /**
   * Hide the hover menu.
   */
  hideHoverMenu(): void {
    this.cancelHideHoverMenu();
    this.hoverMenuHideHandle = scheduleDelayed(
      150,
      () => {
        this.hoverMenuId.set(null);
        this.hoverMenuHideHandle = null;
      },
      this.destroyRef,
    );
  }

  /**
   * Handles a generic hover-menu group (trigger + floating menu) losing focus.
   * Mirrors onFilingGroupFocusOut: only hides when focus truly leaves the
   * group, not when it moves from the trigger into the group's own menu links.
   * @param event
   */
  onHoverGroupFocusOut(event: FocusEvent): void {
    const container = event.currentTarget as HTMLElement;
    const next = event.relatedTarget as Node | null;
    if (!next || !container.contains(next)) {
      this.hideHoverMenu();
    }
  }

  /**
   * Closes a generic hover-menu group on Escape and returns focus to its
   * trigger - the standard disclosure/menu keyboard affordance. Closes
   * immediately (bypassing the mouseleave debounce) since this is an
   * explicit dismissal. The trigger is either the group's own focusable
   * anchor, or (Contact) the `.navbar__filing-trigger` div itself when it
   * carries its own tabindex.
   */
  onHoverGroupEscape(event: Event): void {
    const container = event.currentTarget as HTMLElement;
    this.cancelHideHoverMenu();
    this.hoverMenuId.set(null);
    const trigger = container.querySelector<HTMLElement>(
      '.navbar__filing-trigger a, .navbar__filing-trigger[tabindex]',
    );
    if (trigger) {
      this.suppressTriggerFocusOpen = true;
      trigger.focus();
      this.suppressTriggerFocusOpen = false;
    }
  }

  /**
   * Resets the trial binders states.
   */
  resetTrialBinders(): void {
    this.trialBinderStateService.isRootActive(true);
    this.trialBinderStateService.setSelectedTrialBinderFolder(null);
  }
}
